import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {useHidePostActions} from "@/auth/hooks/useHidePostActions";
import useImageCompressor from "@/hooks/useImageCompressor";
import { useMutateData } from "@/hooks/useMutation";
import usePostData from "@/hooks/usePostData";
import usePostMultipart from "@/hooks/usePostMultipart";
import { convertEuroToCurrency, convertToEuro } from "@/libs/amount";
import {
  AlertTriangle,
  ArrowRightLeft,
  ChevronRight,
  Info,
  Loader2,
  Pencil,
  Plus,
  Trash,
  Upload,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

type CurrencyObj = { code: string; symbol?: string; [k: string]: any };

const VersementTerrain = ({
  currency, // objet devise non-EUR (ex: { code: "USD", symbol: "$", ... })
  currencySelected, // string | null -> code de la devise sélectionnée; null => EUR
  desiredFundings, // laissé tel quel même si non utilisé, pour compat
  funding, // doit contenir .id et .landPayments
  projectIndicators = [],
  loadingProjectIndicators = false,
  onChange,
  readonly = false,
}: {
  currency: CurrencyObj | null;
  currencySelected: string | null;
  desiredFundings?: any;
  funding: any;
  projectIndicators?: any[];
  loadingProjectIndicators?: boolean;
  onChange: (rows: any[]) => void;
  readonly?: boolean;
}) => {
  // Normalisation initiale depuis landPayments
  const [localVersements, setLocalVersements] = useState(
    (funding?.landPayments || []).map((v: any) => ({
      ...v,
      // formatage dates en YYYY-MM-DD
      expectedAt: v.expectedAt ? String(v.expectedAt).slice(0, 10) : null,
      dateMeasure: v.dateMeasure ? String(v.dateMeasure).slice(0, 10) : "",
      dateVerified: v.dateVerified ? String(v.dateVerified).slice(0, 10) : "",
      // 1=Prévu (par défaut), 2=Réalisé, 3=Vérifié
      status:
        v.status === undefined || v.status === null ? 1 : Number(v.status),
      // pièces & FX
      pjVerified:
        v.pjVerified && v.pjVerified.id
          ? v.pjVerified
          : { id: null, name: "", uri: "#" },
      amountConverted: v.amountConverted ?? null,
      oldRate: v.oldRate ?? null,
      dateOldRate: v.dateOldRate ? String(v.dateOldRate).slice(0, 10) : null,
    }))
  );

  // on mémorise le code précédent (string | null)
  const previousCurrencyRef = useRef<string | null>(currencySelected);

  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [originalVersement, setOriginalVersement] = useState<any>(null);
  const [openDetailsIndex, setOpenDetailsIndex] = useState<
    number | string | null
  >(null);
  const [warningPopoverKey, setWarningPopoverKey] = useState<string | null>(
    null,
  );

  const { postData } = usePostData();
  const hidePostActions = useHidePostActions();
  const { mutate: saveData } = useMutateData({
    method: "POST",
    id: "verdeo-fundings-project",
    onSuccess: () => toast("Les données ont été bien mises à jour."),
  });
  const { postMultipart } = usePostMultipart();
  const { handleCompressImage } = useImageCompressor();

  // Helpers devise sélectionnée
  const isFXSelected = currencySelected === (currency?.code || "");
  const symbolForDisplay = isFXSelected
    ? currency?.symbol || currencySelected
    : "€";

  // Conversion visuelle montants selon devise sélectionnée (EUR <-> currency)
  useEffect(() => {
    const convertVersements = async () => {
      const prevCode = previousCurrencyRef.current; // string | null
      const nowCode = currencySelected; // string | null
      const fxCode = currency?.code || ""; // code non-EUR

      // EUR -> FX
      if (prevCode === null && nowCode === fxCode) {
        const converted = await Promise.all(
          localVersements.map(async (v: any) => ({
            ...v,
            amount: await convertEuroToCurrency(v.amount, currency),
          }))
        );
        setLocalVersements(converted);
      }
      // FX -> EUR
      else if (prevCode === fxCode && nowCode === null) {
        const converted = await Promise.all(
          localVersements.map(async (v: any) => ({
            ...v,
            amount: await convertToEuro(v.amount, currency),
          }))
        );
        setLocalVersements(converted);
      }
      // autres cas (inchangé ou pas de devise fournie) => ne rien faire

      previousCurrencyRef.current = nowCode;
    };
    convertVersements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencySelected, currency?.code]);

  // ===== TRI D’AFFICHAGE PAR expectedAt (ASC) =====
  const displayVersements = useMemo(() => {
    const ts = (d?: string | null) => {
      if (!d) return Number.POSITIVE_INFINITY; // sans date prévue -> fin
      const t = Date.parse(d);
      return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
    };
    return [...localVersements].sort((a, b) => {
      const da = ts(a.expectedAt);
      const db = ts(b.expectedAt);
      if (da !== db) return da - db;
      return String(a.id).localeCompare(String(b.id));
    });
  }, [localVersements]);

  const readLinkedLandPaymentId = (value: any): number => {
    if (value === null || value === undefined) return 0;

    if (typeof value === "number") {
      return Number.isFinite(value) && value > 0 ? value : 0;
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return 0;

      const direct = Number(trimmed);
      if (Number.isFinite(direct) && direct > 0) return direct;

      const match = trimmed.match(/(\d+)(?:\/)?$/);
      return match?.[1] ? Number(match[1]) : 0;
    }

    if (typeof value === "object") {
      return readLinkedLandPaymentId(
        value?.id ?? value?.["@id"] ?? value?.["@value"],
      );
    }

    return 0;
  };

  const projectIndicatorVersements = useMemo(() => {
    const normalizeMonth = (month: any) => {
      const n = Number(month);
      if (Number.isNaN(n)) return -1;
      if (n >= 0 && n <= 11) return n;
      if (n >= 1 && n <= 12) return n - 1;
      return -1;
    };

    const formatPeriod = (year: any, month: number) => {
      const parsedYear = Number(year);
      if (!Number.isFinite(parsedYear) || month < 0 || month > 11) {
        return "Periode non renseignee";
      }

      return new Date(parsedYear, month, 1).toLocaleDateString("fr-FR", {
        month: "long",
        year: "numeric",
      });
    };

    return (Array.isArray(projectIndicators) ? projectIndicators : [])
      .map((indicator: any) => {
        const linkedLandPaymentId = readLinkedLandPaymentId(
          indicator?.landPayment,
        );
        const month = normalizeMonth(indicator?.month);
        const year = Number(indicator?.year);
        const amount =
          typeof indicator?.valueDisplay === "number"
            ? Number(indicator.valueDisplay)
            : Number(indicator?.value ?? 0);

        if (linkedLandPaymentId > 0) return null;
        if (!Number.isFinite(amount) || amount <= 0) return null;

        return {
          id: String(indicator?.id ?? `${year}-${month}-${indicator?.type}`),
          amount,
          status: Number(indicator?.type ?? 1),
          periodLabel: formatPeriod(year, month),
          sortKey:
            Number.isFinite(year) && month >= 0
              ? new Date(year, month, 1).getTime()
              : Number.POSITIVE_INFINITY,
          pjs: Array.isArray(indicator?.pjs) ? indicator.pjs : [],
        };
      })
      .filter(Boolean)
      .sort((a: any, b: any) => {
        if (a.sortKey !== b.sortKey) return a.sortKey - b.sortKey;
        if (a.status !== b.status) return a.status - b.status;
        return String(a.id).localeCompare(String(b.id));
      });
  }, [projectIndicators]);

  const displayRows = useMemo(() => {
    const localRows = displayVersements.map((item: any) => ({
      source: "local" as const,
      sortKey: item?.expectedAt
        ? Date.parse(item.expectedAt)
        : Number.POSITIVE_INFINITY,
      item,
    }));

    const indicatorRows = projectIndicatorVersements.map((item: any) => ({
      source: "indicator" as const,
      sortKey: Number(item?.sortKey ?? Number.POSITIVE_INFINITY),
      item,
    }));

    return [...localRows, ...indicatorRows].sort((a, b) => {
      if (a.sortKey !== b.sortKey) return a.sortKey - b.sortKey;
      if (a.source !== b.source) return a.source === "indicator" ? -1 : 1;
      return String(a.item?.id ?? "").localeCompare(String(b.item?.id ?? ""));
    });
  }, [displayVersements, projectIndicatorVersements]);

  // Upload PJ (compress si image, puis envoie) -> pjVerified
  const uploadMedia = async (baseIndex: number, file: File) => {
    if (readonly) return;
    const data = new FormData();
    data.append("type", "12");
    data.append("subType", "20");

    const fileDefaultName = file.name;
    let compressedBlob: Blob | null = null;
    try {
      compressedBlob = await handleCompressImage(file, 1024, 768, 0.9);
    } catch {
      compressedBlob = null;
    }

    if (compressedBlob) {
      const renamedFile = new File([compressedBlob], fileDefaultName, {
        type: compressedBlob.type,
      });
      data.append("files[]", renamedFile);
    } else {
      data.append("files[]", file);
    }

    const newMedias: any = await postMultipart({
      url: `${import.meta.env.VITE_IP_API}/api/media/temp/upload`,
      data,
    });

    if (!Array.isArray(newMedias)) {
      toast(newMedias?.message || "Erreur lors de l'upload");
      return;
    }

    const updated = [...localVersements];
    updated[baseIndex].pjVerified = newMedias[0];
    setLocalVersements(updated);
  };

  // Changement local simple
  const handleChange = (baseIndex: number, key: string, value: any) => {
    if (readonly) return;
    const updated = [...localVersements];
    switch (key) {
      case "amount":
        updated[baseIndex].amount =
          typeof value === "number"
            ? value
            : parseFloat(String(value).replace(",", ".")) || 0;
        break;
      case "expectedAt":
        updated[baseIndex].expectedAt = String(value ?? "");
        break;
      case "status": {
        const next = Number(value); // 1=Prévu, 2=Réalisé, 3=Vérifié
        updated[baseIndex].status = next;
        // reset des champs dépendants pour éviter incohérences
        if (next === 1) {
          // Prévu
          updated[baseIndex].dateMeasure = "";
          updated[baseIndex].dateVerified = "";
          updated[baseIndex].pjVerified = { id: null, name: "", uri: "#" };
        } else if (next === 2) {
          // Réalisé
          updated[baseIndex].dateVerified = "";
          updated[baseIndex].pjVerified = { id: null, name: "", uri: "#" };
        }
        break;
      }
      case "dateMeasure":
        updated[baseIndex].dateMeasure = String(value ?? "");
        break;
      case "dateVerified":
        updated[baseIndex].dateVerified = String(value ?? "");
        break;
      default:
        updated[baseIndex][key] = value;
    }
    setLocalVersements(updated);
  };

  // SAVE (payload adapté aux nouvelles clés)
  const handleSave = async (baseIndex: number) => {
    if (readonly) return;
    const updated = [...localVersements];
    const v = updated[baseIndex];

    if (!v.expectedAt) {
      toast("Veuillez renseigner la date prévue.");
      return;
    }

    // validations selon le statut
    if (v.status === 2 || v.status === 3) {
      if (!v.dateMeasure) {
        toast("Veuillez renseigner la date de réalisation.");
        return;
      }
    }
    if (v.status === 3) {
      if (!v.dateVerified) {
        toast("Veuillez renseigner la date de vérification.");
        return;
      }
      if (!v.pjVerified?.id) {
        toast("Veuillez ajouter une pièce jointe (preuve de vérification).");
        return;
      }
    }

    // Envoi en EUR côté back si la devise non-EUR est sélectionnée
    const amountInEur = isFXSelected
      ? await convertToEuro(v.amount, currency)
      : v.amount;

    const payload: any = {
      realfunding: funding.id,
      amount: amountInEur,
      expectedAt: v.expectedAt,
      status: v.status, // 1=prévu, 2=réalisé, 3=vérifié
      ...(v.status === 2 || v.status === 3
        ? { dateMeasure: v.dateMeasure }
        : {}),
      ...(v.status === 3
        ? { dateVerified: v.dateVerified, pjVerified: v.pjVerified.id }
        : {}),
      ...(String(v.id).startsWith("tmp-") ? {} : { toEdit: v.id }),
    };

    try {
      const res = await postData({
        url: `${import.meta.env.VITE_IP_API}/api/land-payment/save`,
        data: payload,
      });

      if (typeof res?.id !== "undefined") updated[baseIndex].id = res.id;

      setLocalVersements(updated);
      setEditIndex(null);
      setOriginalVersement(null);
      setOpenDetailsIndex(null);
      onChange(updated);

      toast("Le versement a été enregistré.");
    } catch (err) {
      console.error(err);
      toast("Erreur lors de l’enregistrement du versement.");
    }
  };

  // DELETE
  const handleDelete = async (baseIndex: number) => {
    if (readonly) return;
    const confirmDelete = window.confirm(
      "Êtes-vous sûr de vouloir supprimer ce versement ?"
    );
    if (!confirmDelete) return;

    const updated = [...localVersements];
    const toDelete = updated[baseIndex];

    try {
      if (toDelete.id && !String(toDelete.id).startsWith("tmp-")) {
        await saveData({
          endpoint: `${import.meta.env.VITE_IP_API}/api/land-payment/delete/${
            toDelete.id
          }`,
          body: {},
        });
      }
      updated.splice(baseIndex, 1);
      setLocalVersements(updated);
      onChange(updated);
    } catch (err) {
      console.error("Erreur lors de la suppression:", err);
    }
  };

  const handleCancel = (baseIndex: number) => {
    if (readonly) return;
    const updated = [...localVersements];
    if (originalVersement) {
      updated[baseIndex] = originalVersement;
    } else {
      updated.splice(baseIndex, 1);
    }
    setLocalVersements(updated);
    setEditIndex(null);
    setOriginalVersement(null);
    setOpenDetailsIndex(null);
    onChange(updated);
  };

  const addVersement = async () => {
    if (readonly) return;
    if (editIndex !== null) {
      toast("Termine l’édition en cours avant d’ajouter un versement.");
      return;
    }

    const newVersement = {
      id: `tmp-${Date.now()}`,
      amount: 0,
      expectedAt: new Date().toISOString().slice(0, 10), // Date prévue
      status: 1, // Prévu par défaut
      dateMeasure: "",
      dateVerified: "",
      pjVerified: { id: null, name: "", uri: "#" },
      amountConverted: null,
      oldRate: null,
      dateOldRate: null,
    };

    const updated = [...localVersements, newVersement];
    setLocalVersements(updated);
    setEditIndex(updated.length - 1);
    setOriginalVersement(null);
    setOpenDetailsIndex(null);
    onChange(updated);
  };

  // === FX Info (popover) — seulement quand Vérifié (3) et taux dispo
  const RateInfo = ({ v }: { v: any }) => {
    if (v.status !== 3 || !v.oldRate || !v.dateOldRate) return null;
    const d = new Date(v.dateOldRate);
    const dateStr = isNaN(d.getTime())
      ? v.dateOldRate
      : d.toLocaleDateString("fr-FR");
    const targetCode = isFXSelected ? currency?.code || "—" : "EUR";
    const targetSymbol = isFXSelected ? currency?.symbol || targetCode : "€";

    return (
      <>
        {/* Desktop */}
        <div className="hidden sm:inline-flex">
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center text-gray-500 hover:text-gray-700"
                aria-label="Voir le taux utilisé"
                title="Voir le taux utilisé"
              >
                <Info size={14} />
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="bottom"
              align="start"
              className="text-xs w-[320px] max-w-[90vw] p-3"
            >
              <div className="space-y-1">
                <div className="font-medium">Taux utilisé</div>
                <div>
                  1€ ={" "}
                  <strong>
                    {(1 / Number(v.oldRate)).toLocaleString("fr-FR")}{" "}
                    {targetSymbol}
                  </strong>
                </div>
                <div>
                  au <strong>{dateStr}</strong>
                </div>
                {v.amountConverted != null && (
                  <div className="mt-1 text-[11px] text-gray-600">
                    Montant converti:{" "}
                    <strong>
                      {Number(v.amountConverted).toLocaleString("fr-FR")}{" "}
                      {targetSymbol}
                    </strong>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Mobile */}
        <div className="sm:hidden mt-1 text-[11px] bg-gray-100 border border-gray-200 rounded px-2 py-1 text-gray-700">
          <div className="font-medium mb-0.5">Taux utilisé</div>
          <div>
            1€ ={" "}
            <strong>
              {(1 / Number(v.oldRate)).toLocaleString("fr-FR")} {targetSymbol}
            </strong>
          </div>
          <div>
            au <strong>{dateStr}</strong>
          </div>
          {v.amountConverted != null && (
            <div className="mt-0.5">
              Montant converti:{" "}
              <strong>
                {Number(v.amountConverted).toLocaleString("fr-FR")}{" "}
                {targetSymbol}
              </strong>
            </div>
          )}
        </div>
      </>
    );
  };

  // Montant affiché : si devise non-EUR sélectionnée ET statut vérifié(3) => amountConverted si dispo
  const displayAmount = (v: any) => {
    if (isFXSelected) {
      if (v.status === 3 && v.amountConverted != null)
        return Number(v.amountConverted);
      return Number(v.amount);
    }
    return Number(v.amount);
  };

  // Helpers d'affichage statut
  const statusLabel = (s: number) =>
    s === 3 ? "Vérifié" : s === 2 ? "Réalisé" : "Prévu";

  const formatDisplayAmount = (amount: number) =>
    Number(amount || 0).toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const getMediaHref = (media: any) => {
    const mediaUri =
      typeof media?.uri === "string" ? media.uri.trim() : "";
    if (mediaUri) {
      if (/^https?:\/\//i.test(mediaUri)) return mediaUri;
      return `${import.meta.env.VITE_MEDIA_BASE}${mediaUri}`;
    }

    const mediaPath =
      typeof media?.path === "string" ? media.path.trim() : "";
    if (mediaPath) {
      return `${import.meta.env.VITE_IP_API}/media/${mediaPath}`;
    }

    return "";
  };

  const getMediaLabel = (media: any, fallbackIndex?: number) => {
    if (typeof media?.name === "string" && media.name.trim()) {
      return media.name;
    }

    const mediaUri =
      typeof media?.uri === "string" ? media.uri.trim() : "";
    if (mediaUri) {
      return mediaUri.split("/").pop() || `justificatif_${fallbackIndex ?? 0}`;
    }

    const mediaPath =
      typeof media?.path === "string" ? media.path.trim() : "";
    if (mediaPath) {
      return mediaPath.split("/").pop() || `justificatif_${fallbackIndex ?? 0}`;
    }

    return `justificatif_${fallbackIndex ?? 0}`;
  };

  return (
    <div className="mt-4">
      {/* Header + bouton d’ajout */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-gray-900 font-medium flex items-center gap-2">
          <ArrowRightLeft size={18} className="" />
          Versements terrain
        </h2>
        {!readonly && !hidePostActions && (
          <button
            onClick={addVersement}
            className="text-sm text-green-600 hover:underline inline-flex items-center gap-1"
          >
            <Plus size={16} /> Ajouter un versement
          </button>
        )}
      </div>

      {loadingProjectIndicators && (
        <div className="mb-3 flex items-center gap-2 rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>
            Chargement des versements terrain depuis Suivi de performance ESG...
          </span>
        </div>
      )}

      {displayRows.map((entry: any) => {
        const isIndicatorRow = entry.source === "indicator";
        const v = entry.item;
        const baseIndex = !isIndicatorRow
          ? localVersements.findIndex((x: any) => x.id === v.id)
          : -1;
        const detailsKey = isIndicatorRow ? `indicator-${v.id}` : baseIndex;
        const detailsOpen = openDetailsIndex === detailsKey;
        const amountForDisplay = isIndicatorRow
          ? Number(v.amount ?? 0)
          : displayAmount(v);

        return (
          <div
            key={`${entry.source}-${v.id}`}
            className="bg-gray-50 p-3 rounded mb-2"
          >
            {!isIndicatorRow && !readonly && editIndex === baseIndex ? (
              <>
                {/* Formulaire édition */}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-start">
                  {/* Montant */}
                  <div className="relative">
                    <div className="flex items-center gap-1">
                      <label className="text-xs text-gray-600">Montant</label>
                      {isFXSelected && <RateInfo v={v} />}
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={Number.isFinite(v.amount) ? v.amount : 0}
                      onChange={(e) =>
                        handleChange(baseIndex, "amount", e.target.value)
                      }
                      className="mt-1 input w-full border rounded px-2 py-1"
                      disabled={readonly}
                    />
                    <span className="absolute right-2 bottom-[-6px] -translate-y-1/2 text-sm text-gray-600">
                      {symbolForDisplay}
                    </span>
                  </div>

                  {/* Date prévue */}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Date prévue
                    </label>
                    <input
                      type="date"
                      value={v.expectedAt || ""}
                      onChange={(e) =>
                        handleChange(baseIndex, "expectedAt", e.target.value)
                      }
                      className="input w-full border rounded px-2 py-1"
                      disabled={readonly}
                    />
                  </div>

                  {/* Statut (select 1/2/3) */}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Statut
                    </label>
                    <select
                      value={v.status}
                      onChange={(e) =>
                        handleChange(baseIndex, "status", e.target.value)
                      }
                      className="input w-full border rounded px-2 py-1 bg-white"
                      disabled={readonly}
                    >
                      <option value={1}>Prévu</option>
                      <option value={2}>Réalisé</option>
                      <option value={3}>Vérifié</option>
                    </select>
                  </div>

                  {/* Actions */}
                  <div className="flex items-end gap-2 justify-end">
                    {!hidePostActions && (
                      <button
                        onClick={() => handleSave(baseIndex)}
                        className="text-green-600 text-sm font-medium"
                        title="Enregistrer"
                        disabled={readonly}
                      >
                        Save
                      </button>
                    )}
                    <button
                      onClick={() => handleCancel(baseIndex)}
                      className="text-gray-600 text-sm font-medium"
                      title="Annuler"
                      disabled={readonly}
                    >
                      Annuler
                    </button>
                  </div>
                </div>

                {/* Champs dépendants du statut */}
                {(v.status === 2 || v.status === 3) && (
                  <div className="mt-3 rounded border border-gray-200 bg-white p-3 space-y-3">
                    {/* Toujours afficher le bloc "Réalisé" si Réalisé OU Vérifié */}
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Date de réalisation
                      </label>
                      <input
                        type="date"
                        value={v.dateMeasure || ""}
                        onChange={(e) =>
                          handleChange(baseIndex, "dateMeasure", e.target.value)
                        }
                        className="input w-full border rounded px-2 py-1"
                        disabled={readonly}
                      />
                    </div>

                    {v.status === 3 && (
                      <>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Date de vérification
                          </label>
                          <input
                            type="date"
                            value={v.dateVerified || ""}
                            onChange={(e) =>
                              handleChange(
                                baseIndex,
                                "dateVerified",
                                e.target.value
                              )
                            }
                            className="input w-full border rounded px-2 py-1"
                            disabled={readonly}
                          />
                        </div>

                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Pièce jointe (preuve de vérification)
                          </label>
                          {v.pjVerified?.id ? (
                            <div className="flex items-center gap-2">
                              <a
                                href={
                                  import.meta.env.VITE_MEDIA_BASE +
                                  v.pjVerified.uri
                                }
                                target="_blank"
                                className="underline text-sm"
                                rel="noreferrer"
                              >
                                📄 {v.pjVerified.name}
                              </a>
                              {!readonly && (
                                <label className="ml-2 inline-flex items-center gap-1 text-xs cursor-pointer text-blue-700">
                                  <Upload size={14} />
                                  <span>Changer</span>
                                  <input
                                    type="file"
                                    className="hidden"
                                    onChange={(e) => {
                                      if (e.target.files?.[0])
                                        uploadMedia(
                                          baseIndex,
                                          e.target.files[0]
                                        );
                                    }}
                                  />
                                </label>
                              )}
                            </div>
                          ) : (
                            !readonly && (
                              <label className="inline-flex items-center gap-1 text-sm cursor-pointer text-blue-700">
                                <Upload size={16} />
                                <span>Ajouter une pièce jointe</span>
                                <input
                                  type="file"
                                  className="hidden"
                                  onChange={(e) => {
                                    if (e.target.files?.[0])
                                      uploadMedia(baseIndex, e.target.files[0]);
                                  }}
                                />
                              </label>
                            )
                          )}
                        </div>
                      </>
                    )}

                    <p className="text-xs text-gray-500">
                      Astuce : sur petit écran, renseigne d’abord Montant & Date
                      prévue, puis complète les champs de statut.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* LECTURE / LISTING */}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-start">
                  {/* Montant 2 lignes */}
                  <div>
                    <div className="flex items-center gap-1">
                      <strong>Montant</strong>
                      {!isIndicatorRow && isFXSelected && <RateInfo v={v} />}
                    </div>
                    <div className="mt-1">
                      {isIndicatorRow
                        ? formatDisplayAmount(amountForDisplay)
                        : Math.trunc(amountForDisplay).toLocaleString()}{" "}
                      {symbolForDisplay}
                    </div>
                  </div>

                  <div>
                    {isIndicatorRow ? (
                      <>
                        <strong>Période :</strong> {v.periodLabel}
                      </>
                    ) : (
                      <>
                        <strong>Date prévue :</strong>{" "}
                        {v.expectedAt
                          ? new Date(v.expectedAt).toLocaleDateString("fr-FR")
                          : "—"}
                      </>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <strong>Statut :</strong>
                    <span
                      className={
                        v.status === 3
                          ? "text-[#80b534]"
                          : v.status === 2
                          ? "text-[#14ae5c]"
                          : "text-[#59adc5]"
                      }
                    >
                      {statusLabel(v.status)}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 justify-end">
                    {isIndicatorRow ? (
                      <div
                        onMouseEnter={() =>
                          setWarningPopoverKey(String(detailsKey))
                        }
                        onMouseLeave={() => setWarningPopoverKey(null)}
                      >
                        <Popover open={warningPopoverKey === String(detailsKey)}>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex items-center text-amber-600 hover:text-amber-700"
                              aria-label="Information de modification"
                            >
                              <AlertTriangle size={16} />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            align="end"
                            className="w-72 text-xs text-slate-700"
                          >
                            <div className="flex items-start gap-2">
                              <AlertTriangle className="mt-[1px] h-3.5 w-3.5 shrink-0 text-amber-600" />
                              <span>
                                Cette ligne a été saisie dans Suivi de
                                performance ESG. Pour la modifier, allez dans
                                cet onglet.
                              </span>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    ) : !readonly && !hidePostActions && (
                      <>
                        <button
                          onClick={() => {
                            setOriginalVersement({
                              ...localVersements[baseIndex],
                            });
                            setEditIndex(baseIndex);
                            setOpenDetailsIndex(null);
                          }}
                          className="text-sm"
                          title="Modifier"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(baseIndex)}
                          className="text-red-500 text-sm"
                          title="Supprimer"
                        >
                          <Trash size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Panneau détails (read-only) */}
                {(isIndicatorRow
                  ? v.pjs?.length > 0
                  : v.status === 2 || v.status === 3) && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() =>
                        setOpenDetailsIndex(detailsOpen ? null : detailsKey)
                      }
                      className="flex items-center gap-1 text-sm underline text-blue-700"
                    >
                      {isIndicatorRow
                        ? "Voir les justificatifs"
                        : `Détails ${v.status === 2 ? "réalisé" : "vérification"}`}
                      <ChevronRight
                        size={16}
                        className={"ml-1 " + (detailsOpen ? "rotate-90" : "")}
                      />
                    </button>

                    {detailsOpen && (
                      <div className="mt-3 rounded border border-gray-200 bg-white p-3 space-y-2">
                        {isIndicatorRow ? (
                          <div className="space-y-2">
                            {(Array.isArray(v.pjs) ? v.pjs : []).map(
                              (pj: any, index: number) => {
                                const label = getMediaLabel(pj, index + 1);
                                const href = getMediaHref(pj);

                                return href ? (
                                  <div key={pj?.id ?? `indicator-pj-${index}`}>
                                    <a
                                      href={href}
                                      target="_blank"
                                      className="underline"
                                      rel="noreferrer"
                                    >
                                      <strong>PJ :</strong> {label}
                                    </a>
                                  </div>
                                ) : (
                                  <div
                                    key={pj?.id ?? `indicator-pj-${index}`}
                                    className="text-gray-500"
                                  >
                                    <strong>PJ :</strong> {label}
                                  </div>
                                );
                              },
                            )}
                          </div>
                        ) : (
                          <>
                            {/* Toujours afficher le détail "Réalisé" même si Vérifié */}
                            <div>
                              <strong>Date de réalisation :</strong>{" "}
                              {v.dateMeasure
                                ? new Date(v.dateMeasure).toLocaleDateString(
                                    "fr-FR"
                                  )
                                : "—"}
                            </div>

                            {v.status === 3 && (
                              <>
                                <div>
                                  <strong>Date de vérification :</strong>{" "}
                                  {v.dateVerified
                                    ? new Date(v.dateVerified).toLocaleDateString(
                                        "fr-FR"
                                      )
                                    : "—"}
                                </div>
                                <div>
                                  <strong>PJ :</strong>{" "}
                                  {v.pjVerified?.id ? (
                                    <a
                                      href={getMediaHref(v.pjVerified)}
                                      target="_blank"
                                      className="underline"
                                      rel="noreferrer"
                                    >
                                      {getMediaLabel(v.pjVerified, 1)}
                                    </a>
                                  ) : (
                                    <span className="text-gray-500">—</span>
                                  )}
                                </div>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}

      {!loadingProjectIndicators && displayRows.length === 0 && (
        <p className="text-gray-600 italic mt-4">Aucun versement enregistré.</p>
      )}
    </div>
  );
};

export default VersementTerrain;
